import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const WeddingDetailsForm = ({ formData, onUpdate, onNext, onPrevious }) => {
  const [errors, setErrors] = useState({});

  const culturalTraditions = [
    { id: 'hindu', label: 'Hindu Ceremonies', description: 'Traditional Hindu wedding rituals' },
    { id: 'sikh', label: 'Sikh Ceremonies', description: 'Anand Karaj and Sikh traditions' },
    { id: 'muslim', label: 'Muslim Ceremonies', description: 'Nikah and Islamic traditions' },
    { id: 'christian', label: 'Christian Ceremonies', description: 'Indian Christian wedding traditions' },
    { id: 'punjabi', label: 'Punjabi Traditions', description: 'Punjabi cultural celebrations' },
    { id: 'gujarati', label: 'Gujarati Traditions', description: 'Gujarati wedding customs' },
    { id: 'bengali', label: 'Bengali Traditions', description: 'Bengali wedding ceremonies' },
    { id: 'south-indian', label: 'South Indian Traditions', description: 'Tamil, Telugu, Kannada, Malayalam' },
    { id: 'mixed', label: 'Mixed Traditions', description: 'Combination of different cultures' }
  ];

  const handleInputChange = (field, value) => {
    onUpdate({ [field]: value });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleCulturalSelection = (culturalId) => {
    const current = formData.culturalBackground || [];
    const updated = current.includes(culturalId)
      ? current.filter(id => id !== culturalId)
      : [...current, culturalId];
    
    handleInputChange('culturalBackground', updated);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.weddingDate) newErrors.weddingDate = 'Wedding date is required';
    if (!formData.location?.trim()) newErrors.location = 'Wedding location is required';
    if (!formData.culturalBackground?.length) newErrors.culturalBackground = 'Please select at least one cultural tradition';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Wedding Details
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          Help us understand your wedding plans to recommend the best vendors
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Wedding Date *
            </label>
            <Input
              type="date"
              value={formData.weddingDate || ''}
              onChange={(e) => handleInputChange('weddingDate', e.target.value)}
              className={errors.weddingDate ? 'border-error' : ''}
              min={new Date().toISOString().split('T')[0]}
            />
            {errors.weddingDate && (
              <p className="text-error text-sm font-caption mt-1">{errors.weddingDate}</p>
            )}
            {formData.weddingDate && (
              <p className="text-text-secondary text-sm font-caption mt-1">
                {formatDate(formData.weddingDate)}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Expected Guest Count
            </label>
            <select
              value={formData.guestCount || ''}
              onChange={(e) => handleInputChange('guestCount', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="">Select guest count</option>
              <option value="50-100">50-100 guests</option>
              <option value="100-200">100-200 guests</option>
              <option value="200-300">200-300 guests</option>
              <option value="300-500">300-500 guests</option>
              <option value="500+">500+ guests</option>
            </select>
          </div>

          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Wedding Location *
            </label>
            <Input
              type="text"
              placeholder="City, State (e.g., San Francisco, CA)"
              value={formData.location || ''}
              onChange={(e) => handleInputChange('location', e.target.value)}
              className={errors.location ? 'border-error' : ''}
            />
            {errors.location && (
              <p className="text-error text-sm font-caption mt-1">{errors.location}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Estimated Budget
            </label>
            <select
              value={formData.budget || ''}
              onChange={(e) => handleInputChange('budget', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="">Select budget range</option>
              <option value="under-25k">Under $25,000</option>
              <option value="25k-50k">$25,000 - $50,000</option>
              <option value="50k-75k">$50,000 - $75,000</option>
              <option value="75k-100k">$75,000 - $100,000</option>
              <option value="100k-150k">$100,000 - $150,000</option>
              <option value="over-150k">Over $150,000</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              How far along are you?
            </label>
            <select
              value={formData.planningStage || ''}
              onChange={(e) => handleInputChange('planningStage', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="">Select planning stage</option>
              <option value="just-engaged">Just Engaged</option>
              <option value="early-planning">Early Planning (6+ months)</option>
              <option value="mid-planning">Mid Planning (3-6 months)</option>
              <option value="final-planning">Final Planning (1-3 months)</option>
              <option value="last-minute">Last Minute (Less than 1 month)</option>
            </select>
          </div>
        </div>
      </div>

      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Cultural & Religious Preferences
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          Select all traditions that apply to your celebration
        </p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {culturalTraditions.map((tradition) => {
            const isSelected = formData.culturalBackground?.includes(tradition.id);
            
            return (
              <button
                key={tradition.id}
                onClick={() => handleCulturalSelection(tradition.id)}
                className={`p-4 rounded-xl border-2 text-left transition-smooth hover:shadow-soft ${
                  isSelected
                    ? 'border-primary bg-primary-50 shadow-soft'
                    : 'border-border hover:border-primary-200 bg-white'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${
                    isSelected
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-surface-100 text-text-secondary'
                  }`}>
                    <Icon name="Heart" size={20} color="currentColor" />
                  </div>
                  <div className="flex-1">
                    <h4 className={`font-medium text-sm ${
                      isSelected ? 'text-primary' : 'text-text-primary'
                    }`}>
                      {tradition.label}
                    </h4>
                    <p className="text-xs text-text-muted font-caption mt-1">
                      {tradition.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        
        {errors.culturalBackground && (
          <p className="text-error text-sm font-caption mt-2 flex items-center space-x-1">
            <Icon name="AlertCircle" size={16} color="currentColor" />
            <span>{errors.culturalBackground}</span>
          </p>
        )}
      </div>

      <div className="flex justify-between pt-6 border-t border-border">
        <Button
          variant="outline"
          size="lg"
          onClick={onPrevious}
          iconName="ArrowLeft"
          iconSize={18}
        >
          Previous
        </Button>
        
        <Button
          variant="primary"
          size="lg"
          onClick={handleNext}
          iconName="ArrowRight"
          iconPosition="right"
          iconSize={18}
        >
          Continue to Account Setup
        </Button>
      </div>
    </div>
  );
};

export default WeddingDetailsForm;